import React from 'react';
import { Artifact } from '../../../types/artifact';
import { useTheme } from '../../../contexts/ThemeContext';
import { ArtifactoryMarkdown } from '../../artifactory/ArtifactoryMarkdown';

interface DocumentArtifactViewerProps {
  artifact: Artifact;
  mode?: 'default' | 'formatted' | 'raw';
}

export const DocumentArtifactViewer: React.FC<DocumentArtifactViewerProps> = ({ 
  artifact, 
  mode = 'default' 
}) => {
  const { darkMode } = useTheme();

  const getDocumentContent = () => {
    if (typeof artifact.content === 'string') return { text: artifact.content };
    return artifact.content;
  };

  const document = getDocumentContent();

  const renderRaw = () => (
    <pre className={`whitespace-pre-wrap font-mono text-sm p-4 rounded border overflow-auto ${
      darkMode
        ? 'bg-gray-900 border-gray-600 text-gray-200'
        : 'bg-gray-50 border-gray-200 text-gray-800'
    }`}>
      {typeof document === 'string' ? document : JSON.stringify(document, null, 2)}
    </pre>
  );

  const renderFormatted = () => {
    if (document.html) {
      return (
        <div
          className={`prose prose-sm max-w-none ${darkMode ? 'prose-invert' : ''}`}
          dangerouslySetInnerHTML={{ __html: document.html }}
        />
      );
    }
    // {markdown: "..."} shape — produced by the persona-chat
    // create_artifacts pipeline (artifact_type='document' wraps
    // string content into {markdown}). Render via ArtifactoryMarkdown
    // so embedded ```mermaid fences become actual diagrams on the
    // artifact page, not source text or JSON dumps. Checked BEFORE
    // .text so a doc with both fields prefers the rendered version.
    if (typeof document.markdown === 'string') {
      return <ArtifactoryMarkdown content={document.markdown} size="medium" />;
    }
    if (document.text) {
      return (
        <div className={`prose prose-sm max-w-none whitespace-pre-wrap ${
          darkMode ? 'prose-invert text-gray-200' : 'text-gray-800'
        }`}>
          {document.text}
        </div>
      );
    }
    return renderRaw();
  };

  switch (mode) {
    case 'raw': return renderRaw();
    case 'formatted': return renderFormatted();
    default: return renderFormatted();
  }
};
